import React, { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  sx, Shell, Notice, BASE_ERROR_COPY, normalizeReportData, deepSet,
  fmtWhen, fmtDate, ReportHeader, ReportBodySections, BRAND_LINE,
  reportIssues, INCOMPLETE_REPORT_MSG, UnlinkedWorkersPanel, linkWorkerInForm, likelyUnlinkedNames,
} from './reportShared'
import AuditTrail from './reportAudit'
import { etToday } from '../lib/dateUtils'

// Authenticated single-report screen (deep-linked from the shared "approval
// needed" + escalation emails as /report/approve?id=<id>, and from the in-app
// Field Reports list). One page, three modes, all decided SERVER-side and
// handed back as capability flags — the client never infers them from a role:
//
//   can_approve     PM / owner / road_super (and admin once escalated) — review,
//                   optionally edit in place (save-edit), then approve.
//   can_office_enter owner / road_super / the job's PM — fill in an UNSUBMITTED
//                   report and finalize it in one step (office-submit).
//   can_fill_in     the report's own FOREMAN, signed in — fill in / correct and
//                   submit it (foreman-submit). Never approves.
//
// Requires a logged-in session: an un-signed-in visitor is bounced to
// /login?redirect=… and returned here after signing in. There is no no-login
// token approve path. Approval is STATUS-ONLY and records the logged-in user's
// real identity (server-side). Authorized approvers may EDIT the report in
// place first; that edit is a separate, attributed action (save-edit), not a
// side effect of approving.

const ERROR_COPY = {
  ...BASE_ERROR_COPY,
  missing_report: 'This approval link is missing its report id.',
  not_authorized: 'You don’t have permission to approve this report. Ask the PM or an owner to approve it.',
  report_not_found: 'We could not find this report. It may have been removed.',
  report_deleted: 'This report has been removed. Please contact Carla Pereira.',
  already_approved: 'This report has already been approved.',
  already_submitted: 'This report was already submitted — it now goes through the normal approval flow.',
  edit_window_open: 'The foreman can still edit this report, so it can’t be approved yet.',
  incomplete_report: INCOMPLETE_REPORT_MSG,
  invalid_date: 'That Work Date isn’t a real calendar date. Please pick a valid day.',
  report_changed: 'This report changed while you were approving it — reload the page and try again.',
  invalid_token: 'Your session expired — please sign in again.',
  missing_token: 'Your session expired — please sign in again.',
}

export default function ReportApprovePage({ session }) {
  const [params] = useSearchParams()
  const id = params.get('id')
  // ?edit=1 (the Field Reports "Edit" menu item / "Edit Report" button): open a
  // submitted report straight in edit mode instead of the read-only review, so
  // one click on Edit lands on the editor.
  const wantEdit = params.get('edit') === '1'
  const accessToken = session?.access_token || null

  const [phase, setPhase] = useState('loading') // loading | redirecting | ready | approved | error
  const [errorCode, setErrorCode] = useState(null)
  const [report, setReport] = useState(null)
  const [form, setForm] = useState(null)         // editable copy of report_data
  const [savedForm, setSavedForm] = useState(null) // last persisted copy (for cancel)
  const [workDate, setWorkDate] = useState('')     // editable report_date ('YYYY-MM-DD')
  const [savedWorkDate, setSavedWorkDate] = useState('') // last persisted (for cancel)
  const [role, setRole] = useState(null)
  const [canApprove, setCanApprove] = useState(false)
  const [canOfficeEnter, setCanOfficeEnter] = useState(false)
  const [canFillIn, setCanFillIn] = useState(false)
  const [submittedAt, setSubmittedAt] = useState(null)   // foreman's own submit, this visit
  const [costCodes, setCostCodes] = useState([])
  const [scopeItems, setScopeItems] = useState([])   // PlanSwift task -> cost code picker
  const [employees, setEmployees] = useState([])
  const [approvedAt, setApprovedAt] = useState(null)
  const [pdfErr, setPdfErr] = useState(null)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [saveMsg, setSaveMsg] = useState(null)
  const [validate, setValidate] = useState(false)   // show required-field highlights
  // Unlinked-worker hard stop: the server's 400 'unlinked_workers' payload.
  const [unlinked, setUnlinked] = useState(null)     // [{ list, index, name, hours, suggestions }]
  const [linkRoster, setLinkRoster] = useState([])   // every employee, inactive included
  const [linkNote, setLinkNote] = useState(null)
  const [linking, setLinking] = useState(false)
  // true = a submit-and-approve button SUBMITTED the report and HELD the
  // approval (amber); false = an approve-only button was BLOCKED (red).
  const [unlinkedHeld, setUnlinkedHeld] = useState(false)

  // Auth gate + load. Bounce to login if there's no session.
  useEffect(() => {
    let cancelled = false
    if (!id) { setErrorCode('missing_report'); setPhase('error'); return }
    if (!accessToken) {
      const here = `/report/approve?id=${encodeURIComponent(id)}${wantEdit ? '&edit=1' : ''}`
      window.location.replace(`/login?redirect=${encodeURIComponent(here)}`)
      setPhase('redirecting')
      return
    }
    async function load() {
      try {
        const r = await fetch(`/api/field-reports?action=get&report_id=${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        const data = await r.json()
        if (cancelled) return
        if (!r.ok || !data.ok) { setErrorCode(data.error || 'db_error'); setPhase('error'); return }
        const norm = normalizeReportData(data.report.report_data)
        setReport(data.report)
        setForm(norm)
        setSavedForm(norm)
        setWorkDate(data.report.report_date || '')
        setSavedWorkDate(data.report.report_date || '')
        setRole(data.role)
        setCanApprove(!!data.can_approve)
        setCanOfficeEnter(!!data.can_office_enter)
        setCanFillIn(!!data.can_fill_in)
        setCostCodes(Array.isArray(data.cost_codes) ? data.cost_codes : [])
        setScopeItems(Array.isArray(data.scope_items) ? data.scope_items : [])
        setEmployees(Array.isArray(data.employees) ? data.employees : [])
        if (data.report.pm_approval_status === 'approved') {
          setApprovedAt(data.report.pm_approved_at)
          setPhase('approved')
        } else {
          setPhase('ready')
          if (wantEdit && data.can_approve && !data.can_fill_in && data.report.foreman_submitted_at) setEditing(true)
        }
      } catch (e) {
        if (!cancelled) { setErrorCode('db_error'); setPhase('error') }
      }
    }
    load()
    return () => { cancelled = true }
  }, [id, accessToken, wantEdit])

  const set = useCallback((path, value) => {
    setForm(prev => deepSet(prev, path, value))
  }, [])

  async function handleSave() {
    setSaving(true); setActionError(null); setSaveMsg(null)
    try {
      const r = await fetch('/api/field-reports?action=save-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ report_id: id, report_data: form, report_date: workDate }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) { setActionError(ERROR_COPY[data.error] || ERROR_COPY.db_error); return }
      setSavedForm(form)
      setSavedWorkDate(workDate)
      // Keep the header (and the future-date warning) in sync with the saved date.
      setReport(prev => (prev ? { ...prev, report_date: workDate } : prev))
      setEditing(false)
      setSaveMsg('Your changes were saved.')
    } catch (e) {
      setActionError(ERROR_COPY.db_error)
    } finally {
      setSaving(false)
    }
  }

  // Show the "Link to employee" block from a 400 'unlinked_workers' response
  // (approve BLOCKED) or a 200 held:true one (office entry SUBMITTED, approval
  // HELD). The server returns ITS copy of report_data (exact-name auto-links
  // applied), so the row indexes in `unlinked` match the form we adopt.
  // `persisted` = the server already wrote that report_data (approve, or a hold).
  function showUnlinked(data, persisted, held = false) {
    setUnlinkedHeld(held)
    if (data.report_data) {
      const norm = normalizeReportData(data.report_data)
      setForm(norm)
      if (persisted) setSavedForm(norm)
    }
    setUnlinked(Array.isArray(data.unlinked) ? data.unlinked : [])
    setLinkRoster(Array.isArray(data.roster) ? data.roster : [])
    setLinkNote(null)
    setActionError(null)
  }

  // Link one row to a roster employee. Approve mode saves it straight away
  // through the existing save-edit path; office entry keeps it in the form —
  // the next Enter & Submit carries it.
  async function linkWorker(entry, emp, mode) {
    const next = linkWorkerInForm(form, entry, emp)
    if (!next) { setActionError('This report changed since it was checked — reload the page and try again.'); return }
    const remaining = (unlinked || []).filter(u => !(u.list === entry.list && u.index === entry.index))
    const again = mode === 'office' ? 'Enter & Submit Report' : 'Approve Report'
    if (mode === 'office') {
      setForm(next)
      setUnlinked(remaining)
      setLinkNote(remaining.length ? null : `All workers linked — tap ${again} again.`)
      return
    }
    setLinking(true); setActionError(null)
    try {
      const r = await fetch('/api/field-reports?action=save-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ report_id: id, report_data: next, report_date: workDate }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) { setActionError(ERROR_COPY[data.error] || ERROR_COPY.db_error); return }
      setForm(next)
      setSavedForm(next)
      setUnlinked(remaining)
      setLinkNote(remaining.length ? null : `All workers linked — tap ${again} again.`)
    } catch (e) {
      setActionError(ERROR_COPY.db_error)
    } finally {
      setLinking(false)
    }
  }

  function handleCancelEdit() {
    setForm(savedForm)
    setWorkDate(savedWorkDate)
    setEditing(false)
    setActionError(null)
  }

  // Office entry: fill-and-finalize an unsubmitted report in one step.
  async function handleOfficeSubmit() {
    setActionError(null); setSaveMsg(null)
    // Hard block: office entry finalizes → must be complete.
    if (!reportIssues(form).ok) { setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
    setSubmitting(true)
    try {
      const r = await fetch('/api/field-reports?action=office-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ report_id: id, report_data: form }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) {
        if (data.error === 'incomplete_report') { setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
        if (data.error === 'unlinked_workers') { showUnlinked(data, false); return }
        setActionError(ERROR_COPY[data.error] || ERROR_COPY.db_error); return
      }
      if (data.held) {
        // SUBMITTED, approval HELD — not an error. The report is now a normal
        // pending report, so the page drops out of office entry into approve
        // mode (for an approver), with the link control under the amber notice.
        showUnlinked(data, true, true)
        setReport(prev => (prev ? { ...prev, foreman_submitted_at: data.submitted_at || new Date().toISOString(), pm_approval_status: 'pending' } : prev))
        return
      }
      setUnlinked(null); setLinkNote(null); setUnlinkedHeld(false)
      setApprovedAt(data.approved_at || new Date().toISOString())
      setPhase('approved')
    } catch (e) {
      setActionError(ERROR_COPY.db_error)
    } finally {
      setSubmitting(false)
    }
  }

  // Foreman self-service submit. Every foreman save IS a submission (the same
  // rule as the emailed edit link), and the report stays editable until the PM
  // approves — so this button is "Submit" the first time and "Submit again"
  // after, never a separate save.
  async function handleForemanSubmit() {
    setActionError(null); setSaveMsg(null)
    if (!reportIssues(form).ok) { setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
    setSubmitting(true)
    try {
      const r = await fetch('/api/field-reports?action=foreman-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ report_id: id, report_data: form }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) {
        if (data.error === 'incomplete_report') { setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
        setActionError(ERROR_COPY[data.error] || ERROR_COPY.db_error); return
      }
      const at = data.submitted_at || new Date().toISOString()
      setSubmittedAt(at)
      setSavedForm(form)
      setReport(prev => (prev ? { ...prev, foreman_submitted_at: at } : prev))
    } catch (e) {
      setActionError(ERROR_COPY.db_error)
    } finally {
      setSubmitting(false)
    }
  }

  async function downloadPdf() {
    try {
      const r = await fetch(`/api/field-reports?action=pdf&report_id=${encodeURIComponent(report.id)}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) { setPdfErr('PDF download failed — try again in a moment.'); return }
      setPdfErr(null)
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Field-Report-${report.job_number || report.id}-${report.report_date || ''}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) {
      setPdfErr('PDF download failed: ' + e.message)
    }
  }

  async function handleApprove() {
    setActionError(null); setSaveMsg(null)
    // Block finalize if incomplete — drop into edit mode so the approver can fix
    // the highlighted rows (cost codes + open crew slots), then approve.
    if (!reportIssues(form).ok) { setEditing(true); setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
    setSubmitting(true)
    try {
      const r = await fetch('/api/field-reports?action=approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ report_id: id }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) {
        if (data.error === 'incomplete_report') { setEditing(true); setValidate(true); setActionError(INCOMPLETE_REPORT_MSG); return }
        if (data.error === 'unlinked_workers') { showUnlinked(data, true, false); return }
        if (data.error === 'edit_window_open') {
          setReport(prev => ({ ...prev, foreman_edit_window_ends_at: data.edit_window_ends_at }))
        }
        setActionError(ERROR_COPY[data.error] || ERROR_COPY.db_error)
        return
      }
      setUnlinked(null); setLinkNote(null); setUnlinkedHeld(false)
      setApprovedAt(data.approved_at || new Date().toISOString())
      setPhase('approved')
    } catch (e) {
      setActionError(ERROR_COPY.db_error)
    } finally {
      setSubmitting(false)
    }
  }

  // This page is standalone chrome — no app header, no tabs — so it needs its own
  // way back into the app. The Dashboard's tabs are component state, not routed,
  // so the way back is the ?tab=field-reports deep link (whitelisted there).
  // Deliberately NOT gated on `editing`: same semantics as the browser back
  // button, and Cancel already exists for backing out of an edit.
  function backToFieldReports() {
    window.location.href = '/?tab=field-reports'
  }
  const backLinkStyle = {
    padding: 0, border: 'none', background: 'none', color: '#4a90d9',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  }

  if (phase === 'loading') return <Notice icon="⏳" title="Loading report…" msg="One moment." />
  if (phase === 'redirecting') return <Notice icon="🔐" title="Sign in required" msg="Taking you to sign in…" />
  if (phase === 'error') {
    return (
      <>
        <Notice icon="⚠️" title="Can’t open this report" msg={ERROR_COPY[errorCode] || 'Something went wrong. Please try again.'} />
        {/* Signed-out visitors never reach here (they're bounced to /login), so
            this only shows when there IS a session to go back to. */}
        {accessToken && (
          <div style={{ textAlign: 'center', padding: '0 24px 32px' }}>
            <button onClick={backToFieldReports} style={backLinkStyle}>← Back to Field Reports</button>
          </div>
        )}
      </>
    )
  }

  const approved = phase === 'approved'
  const submitted = !!report?.foreman_submitted_at
  // Office-entry mode: an authorized office user (owner/road_super/PM) filling in
  // an UNSUBMITTED report. The form is editable and submit fills-and-finalizes.
  const officeEntry = !approved && !submitted && canOfficeEnter
  // Pre-submit hint for the submit-and-approve button: who the CLIENT can already
  // tell isn't linked (advisory — the server decides, and never blocks submit).
  const preUnlinked = officeEntry ? likelyUnlinkedNames(form, employees) : []
  // Foreman mode: this report's own foreman, filling it in or correcting it.
  // Takes precedence over office entry (a foreman never has can_office_enter).
  const foremanEntry = !approved && canFillIn
  const noForeman = !report?.foreman_name && !report?.foreman_email
  const ro = officeEntry || foremanEntry ? false : !editing
  // Edit-time warning: this report's Work Date is after today (Eastern). String
  // compare only — both sides are 'YYYY-MM-DD'.
  const workDateFuture = !!report?.report_date && report.report_date > etToday()

  return (
    <Shell>
      <ReportHeader report={report} />

      <div style={sx.body}>
        {accessToken && (
          <div style={{ margin: '0 0 14px' }}>
            <button onClick={backToFieldReports} style={backLinkStyle}>← Back to Field Reports</button>
          </div>
        )}
        {!approved && workDateFuture && (
          <div style={sx.banner('#fff8f0', '#e8820c', '#92510a')}>
            ⚠️ This report’s Work Date (<strong>{fmtDate(report.report_date)}</strong>) is in the future. Make sure you’re reporting work for that day, not today.
          </div>
        )}
        {approved && (
          <div style={sx.banner('#eaf6ec', '#1e7e34', '#1e5128')}>
            ✅ <strong>Approved</strong>{approvedAt ? ` ${fmtWhen(approvedAt)}` : ''}. This report has been released to payroll.
            <div style={{ marginTop: 8 }}>
              <button onClick={downloadPdf}
                style={{ padding: '7px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #1e7e34', borderRadius: 8, background: '#fff', color: '#1e7e34', cursor: 'pointer' }}>
                ⬇ Download PDF
              </button>
              <button onClick={backToFieldReports}
                style={{ marginLeft: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #1e7e34', borderRadius: 8, background: '#fff', color: '#1e7e34', cursor: 'pointer' }}>
                ← Back to Field Reports
              </button>
              {pdfErr && <span style={{ marginLeft: 10, color: '#a12622', fontSize: 12.5 }}>{pdfErr}</span>}
            </div>
          </div>
        )}
        {officeEntry && (
          <div style={sx.banner('#f0f7ff', '#4a90d9', '#1f5b8f')}>
            📝 <strong>No submission yet{noForeman ? ' — no foreman on this job' : ''}.</strong> Fill in the crew’s actual hours and details below, then submit to finalize and release it to payroll.
          </div>
        )}
        {foremanEntry && !submittedAt && (
          <div style={sx.banner('#f0f7ff', '#4a90d9', '#1f5b8f')}>
            {submitted
              ? <>Review your report and fix anything that’s wrong, then tap <strong>Submit again</strong> at the bottom. You can keep editing until your PM approves it.</>
              : <>Fill in the <strong>actual hours and details</strong> for this day’s work, then tap <strong>Submit Daily Report</strong> at the bottom. Cost codes are pre-filled — change them if they’re wrong.</>}
          </div>
        )}
        {foremanEntry && submittedAt && (
          <div style={sx.banner('#eaf6ec', '#1e7e34', '#1e5128')}>
            ✅ <strong>Report submitted</strong> {fmtWhen(submittedAt)}. Your PM will review and approve it. You can keep editing until then.
          </div>
        )}
        {!approved && !officeEntry && !foremanEntry && !canApprove && (
          <div style={sx.banner('#f9f9f9', '#999', '#555')}>
            You can view this report, but you don’t have permission to approve it.
          </div>
        )}
        {!approved && !officeEntry && canApprove && !editing && (
          <div style={sx.banner('#f0f7ff', '#4a90d9', '#1f5b8f')}>
            Review the report below, then approve at the bottom. Need to fix something? Tap <strong>Edit report</strong> first — your changes are saved under your name.
          </div>
        )}
        {!approved && !officeEntry && canApprove && editing && (
          <div style={sx.banner('#fff8f0', '#e8820c', '#92510a')}>
            ✏️ <strong>Editing.</strong> Make your corrections, then <strong>Save changes</strong>. Saving does not approve the report.
          </div>
        )}
        {saveMsg && <div style={sx.banner('#eaf6ec', '#1e7e34', '#1e5128')}>{saveMsg}</div>}

        <AuditTrail report={report} />

        {!approved && !officeEntry && canApprove && editing && (
          <div style={{ margin: '0 0 16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 }}>Work Date</label>
            <input
              type="date"
              style={{ ...sx.input, maxWidth: 200 }}
              value={workDate || ''}
              onChange={e => setWorkDate(e.target.value)}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              The payroll day this work belongs to. Change only if the report landed on the wrong day.
            </div>
          </div>
        )}

        <ReportBodySections report={report} form={form} ro={ro} set={set} costCodes={costCodes} fillIn={officeEntry || (foremanEntry && !submitted)} employees={employees} validate={validate} scopeItems={scopeItems} />
      </div>

      {!approved && (
        <div style={sx.footer}>
          {!foremanEntry && (unlinkedHeld && !officeEntry && !canApprove ? (
            // Held, but this user can't approve (e.g. a PM who created an ad-hoc
            // report on someone else's job): say so, with no link control —
            // linking saves through save-edit, which is approver-only.
            (unlinked || []).length > 0 && (
              <div style={{ ...sx.banner('#fff8f0', '#e8820c', '#92510a'), marginTop: 0 }}>
                <strong>Submitted - NOT approved yet.</strong> {(unlinked || []).map(u => u.name).join(', ')} {(unlinked || []).length === 1 ? 'isn’t' : 'aren’t'} linked to a payroll employee number, so these hours can’t go to payroll. Carla has been emailed. The job’s PM or an owner links them and approves.
              </div>
            )
          ) : (
            <UnlinkedWorkersPanel unlinked={unlinked} roster={linkRoster} busy={linking || submitting} note={linkNote}
              held={unlinkedHeld} onLink={(entry, emp) => linkWorker(entry, emp, officeEntry ? 'office' : 'save')} />
          ))}
          {actionError && (
            <div style={{ ...sx.banner('#fdecec', '#d9534f', '#a12622'), marginTop: 0 }}>{actionError}</div>
          )}
          {foremanEntry ? (
            <>
              <button
                style={sx.actionBtn(submitting)}
                disabled={submitting}
                onClick={handleForemanSubmit}
              >
                {submitting ? 'Submitting…' : submitted ? 'Submit again' : 'Submit Daily Report'}
              </button>
              <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 10 }}>
                Once you submit, your PM reviews and approves. You can keep editing until then.
              </div>
            </>
          ) : officeEntry ? (
            <>
              <button
                style={sx.actionBtn(submitting)}
                disabled={submitting || saving}
                onClick={handleOfficeSubmit}
              >
                {submitting ? 'Submitting…' : 'Enter & Submit Report'}
              </button>
              {/* Pre-fill without finalizing: save report_data only (save-edit),
                  leaving the report unsubmitted for the foreman to complete. */}
              <button
                style={{ ...sx.actionBtn(saving || submitting, '#2563eb', '#9bb4e6'), marginTop: 10 }}
                disabled={saving || submitting}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save (still pending)'}
              </button>
              <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 10 }}>
                {preUnlinked.length > 0
                  ? <><strong style={{ color: '#b45309' }}>{preUnlinked.join(', ')} {preUnlinked.length === 1 ? 'isn’t' : 'aren’t'} in payroll yet - this will submit the report but approval waits until they’re linked.</strong>{' '}</>
                  : <><strong>Enter &amp; Submit</strong> finalizes this report and releases it to payroll under your name. </>}
                <strong>Save (still pending)</strong> just saves your pre-filled cost codes and notes — the report stays open for the foreman to finish and submit.
              </div>
            </>
          ) : editing ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...sx.actionBtn(saving, '#1e7e34'), flex: 1 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button
                style={{ ...sx.actionBtn(saving, '#6b7280', '#b9bfc7'), flex: '0 0 38%' }}
                disabled={saving} onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button
                style={sx.actionBtn(!canApprove || submitting)}
                disabled={!canApprove || submitting}
                onClick={handleApprove}
              >
                {submitting ? 'Approving…' : !canApprove ? 'Approval not permitted' : 'Approve Report'}
              </button>
              {canApprove && (
                <button
                  style={{ ...sx.actionBtn(submitting, '#2563eb', '#9bb4e6'), marginTop: 10 }}
                  disabled={submitting}
                  onClick={() => { setEditing(true); setSaveMsg(null); setActionError(null) }}
                >
                  Edit report
                </button>
              )}
              {canApprove && (
                <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 10 }}>
                  Approving confirms the report is accurate and releases it to payroll. It does not change the submitted hours.
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div style={sx.brand}>{BRAND_LINE}</div>
    </Shell>
  )
}
